import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { Subscription } from 'rxjs';
import { DropdownCoordinatorService } from '../../../services/dropdown-coordinator.service';

export type SortBy = 'created_at' | 'person_name' | 'company_name' | 'deal_name';
export type SortDir = 'asc' | 'desc';

export interface SortOption {
  sort_by: SortBy;
  sort_dir: SortDir;
}

export const DEFAULT_SORT: SortOption = { sort_by: 'created_at', sort_dir: 'desc' };

export interface SortChoice {
  label: string;
  sort_by: SortBy;
  sort_dir: SortDir;
}

/**
 * The choice list used by the Reports page. Exported so other pages can
 * derive their own list (CRM Contacts/Companies need different subsets).
 */
export const REPORTS_SORT_CHOICES: SortChoice[] = [
  { label: 'Date created (newest first)', sort_by: 'created_at',   sort_dir: 'desc' },
  { label: 'Date created (oldest first)', sort_by: 'created_at',   sort_dir: 'asc'  },
  { label: 'Person Name A → Z',           sort_by: 'person_name',  sort_dir: 'asc'  },
  { label: 'Person Name Z → A',           sort_by: 'person_name',  sort_dir: 'desc' },
  { label: 'Company Name A → Z',          sort_by: 'company_name', sort_dir: 'asc'  },
  { label: 'Company Name Z → A',          sort_by: 'company_name', sort_dir: 'desc' },
];

@Component({
  selector: 'app-reports-sort',
  standalone: true,
  imports: [NgForOf, NgIf, NgClass],
  templateUrl: './reports-sort.component.html',
  styleUrl: './reports-sort.component.scss'
})
export class ReportsSortComponent implements OnInit, OnDestroy {

  @Input() value: SortOption = DEFAULT_SORT;
  /** Override the displayed options. Defaults to the full Reports list. */
  @Input() choices: SortChoice[] = REPORTS_SORT_CHOICES;
  @Output() sortChanged = new EventEmitter<SortOption>();

  open = false;

  private readonly id = Symbol('reports-sort');
  private coordinatorSub?: Subscription;

  constructor(
    private host: ElementRef<HTMLElement>,
    private coordinator: DropdownCoordinatorService
  ) {}

  ngOnInit(): void {
    this.coordinatorSub = this.coordinator.current$.subscribe(activeId => {
      if (activeId !== this.id && this.open) {
        this.open = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.coordinatorSub?.unsubscribe();
    this.coordinator.close(this.id);
  }

  get currentLabel(): string {
    const match = this.choices.find(
      c => c.sort_by === this.value.sort_by && c.sort_dir === this.value.sort_dir
    );
    return match ? match.label : this.choices[0].label;
  }

  toggle(event: MouseEvent): void {
    event.stopPropagation();
    if (this.open) {
      this.open = false;
      this.coordinator.close(this.id);
    } else {
      this.open = true;
      this.coordinator.open(this.id);
    }
  }

  select(choice: SortChoice): void {
    this.open = false;
    this.coordinator.close(this.id);
    if (choice.sort_by === this.value.sort_by && choice.sort_dir === this.value.sort_dir) {
      return;
    }
    this.value = { sort_by: choice.sort_by, sort_dir: choice.sort_dir };
    this.sortChanged.emit(this.value);
  }

  isActive(choice: SortChoice): boolean {
    return choice.sort_by === this.value.sort_by && choice.sort_dir === this.value.sort_dir;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.open) {
      return;
    }
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.open = false;
      this.coordinator.close(this.id);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) {
      this.open = false;
      this.coordinator.close(this.id);
    }
  }
}
